import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-subscriptions";
const TITLE = "Shopify Subscriptions: Abo-Verkauf im eigenen Shop";
const DESCRIPTION =
  "Abos in Shopify verkaufen: Subscription-Apps, Voraussetzungen bei Zahlungsarten, Gebühren und Gestaltung der Abo-Angebote. Ehrliche Einordnung für deutsche Shops.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Abos lassen sich in Shopify über Subscription-Apps wie Appstle, Recharge oder Seal Subscriptions verkaufen: Der Kunde wählt Intervall und Menge, die App erzeugt wiederkehrende Bestellungen und zieht die Zahlung automatisch ein. Voraussetzung ist eine Zahlungsart mit wiederkehrender Belastung, etwa Shopify Payments, PayPal oder Kreditkarte. Kauf auf Rechnung und Vorkasse funktionieren für Abos nicht.";

const faqs = [
  {
    q: "Welche Zahlungsarten funktionieren für Shopify-Abos?",
    a: "Wiederkehrende Zahlungen brauchen eine hinterlegbare Zahlungsart: Shopify Payments mit Kreditkarte, PayPal oder vergleichbare Anbieter. Kauf auf Rechnung und Vorkasse scheiden für echte Abos aus, weil keine automatische Folgebelastung möglich ist.",
  },
  {
    q: "Welche Subscription-App ist empfehlenswert?",
    a: "Appstle und Seal Subscriptions decken die meisten Anforderungen zu fairen Preisen ab, Recharge ist die teurere Enterprise-Variante mit mehr Analysefunktionen. Entscheidend sind Intervall-Logik, Kundenportal und Kompatibilität mit Ihrem Theme.",
  },
  {
    q: "Was kosten Abo-Apps für Shopify?",
    a: "Einstiegstarife beginnen bei etwa 10 bis 20 € im Monat und steigen mit der Zahl der aktiven Abos. Manche Apps nehmen zusätzlich einen prozentualen Anteil am Abo-Umsatz, das gehört in die Kalkulation.",
  },
  {
    q: "Wie gestalte ich das Abo-Angebot auf der Produktseite?",
    a: "Der Abo-Vorteil muss sofort sichtbar sein: Ersparnis gegenüber dem Einzelkauf, klarer Intervall, jederzeit kündbar. Die Umschaltung zwischen Einzelkauf und Abo gehört an den Preis, nicht in eine separate Sektion.",
  },
  {
    q: "Lohnt sich ein Abo-Modell für mein Sortiment?",
    a: "Abos tragen sich bei Verbrauchsprodukten mit natürlichem Wiederbestellrhythmus: Kaffee, Pflege, Tiernahrung, Verbrauchsmaterial. Bei selten gekauften Produkten erzeugt ein Abo vor allem Pflegeaufwand.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-subscriptions")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Conversion",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Conversion"
      title="Shopify Subscriptions: Abo-Umsatz im eigenen Shop aufbauen"
      intro="Wiederkehrender Umsatz ist der stabilstete Umsatz. Dieser Überblick zeigt, wie Abos in Shopify technisch laufen, welche Zahlungsarten nötig sind und woran Abo-Angebote auf der Produktseite gemessen werden."
      source="ratgeber-shopify-subscriptions"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Wie Abos in Shopify technisch laufen",
          p: [
            "Shopify selbst liefert die Selling-Plan-Grundlage, die Umsetzung übernehmen Subscription-Apps. Sie definieren Intervalle, erzeugen Folgebestellungen automatisch und stellen ein Kundenportal, in dem Kunden pausieren, wechseln oder kündigen.",
            "Die Abo-Bestellung läuft durch den normalen Checkout und erscheint wie jede andere Bestellung im Admin, nur eben wiederkehrend und ohne erneutes Eingreifen des Kunden.",
          ],
        },
        {
          h: "Zahlungsarten: die harte Grenze",
          p: [
            "Wiederkehrende Belastung braucht eine hinterlegbare Zahlungsart. Shopify Payments mit Kreditkarte und PayPal funktionieren, Kauf auf Rechnung und Vorkasse nicht. Das ist für deutsche Shops relevant, weil Rechnungskauf hier stark ist.",
            "Pragmatische Lösung: Abo mit Karte oder PayPal anbieten, Einzelkauf weiter mit Rechnung. Auf der Produktseite sauber trennen, statt Kunden erst im Checkout scheitern zu lassen.",
          ],
        },
        {
          h: "Was das Abo-Angebot verkauft",
          p: [
            "Die Technik ist selten das Problem. Entschieden wird auf der Produktseite.",
          ],
          list: [
            "Ersparnis gegenüber dem Einzelkauf als konkrete Zahl am Preis",
            "Intervall-Auswahl direkt beim Kaufbutton, nicht auf einer separaten Seite",
            "Kündbarkeit und Pausieren klar benannt, das senkt die Einstiegshürde spürbar",
            "Erste Lieferung schnell, Folgelieferungen planbar kommuniziert",
          ],
        },
        {
          h: "Kosten und Kalkulation",
          p: [
            "Abo-Apps kosten je nach Tarif etwa 10 bis 100 € im Monat, teils plus Umsatzbeteiligung. Rechnen Sie die App-Kosten gegen die Marge der Abo-Bestellungen, nicht gegen den Gesamtumsatz.",
            "Abos verändern auch die Logistik: Folgebestellungen laufen automatisch und sollten in Warenwirtschaft und Fulfillment sauber ankommen, bevor Sie skalieren.",
          ],
        },
        {
          h: "Einrichtung und Optimierung",
          p: [
            "Ich richte die Subscription-App ein, passe die Abo-Darstellung im Theme an und messe die Wirkung auf Warenkorb und Wiederkehrrate. Theme-Arbeit zu 110 € netto pro Stunde, umfassendere Conversion-Projekte ab 3.990 € netto zum Festpreis.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung ab 3.990 €" },
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung und Entwicklung" },
        { to: "/blog/shopify-zahlungsarten-deutschland", label: "Zahlungsarten für deutsche Shops" },
        { to: "/kontakt", label: "Abo-Setup anfragen" },
      ]}
    />
  );
}
